import { useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Delete, Phone, Plus } from 'lucide-react';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — JS module, Phase 4 cleanup
import { useDialer } from '@/contexts/DialerContext';
import { jouerTonaliteDTMF } from '@/state/dtmfTone';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { listDeviceCalls } from '@/lib/twilioDevice';

const KEY_LABELS: Record<string, string> = {
  '2': 'ABC',
  '3': 'DEF',
  '4': 'GHI',
  '5': 'JKL',
  '6': 'MNO',
  '7': 'PQRS',
  '8': 'TUV',
  '9': 'WXYZ',
};

const KEYPAD_ROWS: string[][] = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  ['*', '0', '#'],
];

interface DialerCtx {
  showKeypad: boolean;
  setShowKeypad: (open: boolean) => void;
  dtmfInput: string;
  setDtmfInput: (value: string | ((prev: string) => string)) => void;
  deviceRef: unknown;
  handleCallProspect: (prospect: {
    prospect_name: string;
    prospect_phone: string;
    prospect_company: string;
    id: string | null;
  }) => void;
  // #85 -- selecteur de numero appelant dans le clavier. Les trois champs
  // viennent de DialerContextValue (cf. DialerContext.d.ts:226-233) ; on les
  // declare ici comme le fait chaque consommateur du dialer.
  activeCallerNumbers: CallerNumberLite[];
  selectedCallerId: string | null;
  setSelectedCallerId: (value: string | null) => void;
}

/** Forme minimale d'un numero appelant — le type complet vit dans
 *  DialerContext.d.ts (TwilioNumber) mais n'est pas re-exporte par le .tsx :
 *  chaque consommateur du dialer declare localement ce qu'il consomme. */
interface CallerNumberLite {
  phoneNumber: string;
  friendlyName?: string | null;
}

interface TwilioCallLite {
  sendDigits(digit: string): void;
}
interface TwilioDeviceLike {
  calls?: { size?: number; forEach: (cb: (c: TwilioCallLite) => void) => void };
}

export default function DTMFKeypad() {
  const { t } = useTranslation(['dialer', 'common']);
  const { showKeypad, setShowKeypad, dtmfInput, setDtmfInput, deviceRef, handleCallProspect,
    activeCallerNumbers = [], selectedCallerId, setSelectedCallerId } =
    useDialer() as DialerCtx;

  const sendDTMF = useCallback(
    (digit: string) => {
      // #23 -- retour sonore local. Mesure avant correctif : 0 oscillateur
      // emis a la frappe. Twilio recoit bien le chiffre mais ne renvoie aucun
      // echo audible a l'agent ; il n'y avait rien a entendre.
      jouerTonaliteDTMF(digit);
      setDtmfInput((prev) => prev + digit);
      if (deviceRef) {
        try {
          const calls = listDeviceCalls<{ sendDigits: (d: string) => void }>((window as unknown as { __twilioDevice?: TwilioDeviceLike }).__twilioDevice);
          if (calls.length > 0) {
            calls.forEach((call) => {
              try {
                call.sendDigits(digit);
              } catch {
                /* per-call swallow */
              }
            });
          }
        } catch {
          /* device not ready */
        }
      }
    },
    [deviceRef, setDtmfInput],
  );

  const handleBackspace = useCallback(() => {
    setDtmfInput((prev) => prev.slice(0, -1));
  }, [setDtmfInput]);

  const handleClear = useCallback(() => {
    setDtmfInput('');
  }, [setDtmfInput]);

  const handleManualDial = useCallback(() => {
    if (!dtmfInput || dtmfInput.length < 4) return;
    if (!deviceRef) {
      toast.error(t('dialer:dtmfKeypad.deviceNotReady'));
      return;
    }
    handleCallProspect({
      prospect_name: dtmfInput,
      prospect_phone: dtmfInput,
      prospect_company: '',
      id: null,
    });
    setDtmfInput('');
    setShowKeypad(false);
  }, [dtmfInput, deviceRef, handleCallProspect, setDtmfInput, setShowKeypad]);

  const handleAddDigit = useCallback(() => {
    setDtmfInput((prev) => prev + '+');
  }, [setDtmfInput]);

  return (
    <AnimatePresence>
      {showKeypad && (
        <motion.div
          key="dtmf-keypad"
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 40, scale: 0.95 }}
          transition={{ type: 'spring', stiffness: 340, damping: 28 }}
          style={{
            position: 'fixed',
            bottom: 100,
            right: 16,
            width: 220,
            zIndex: 6000,
          }}
          className="bg-card border border-border rounded-2xl shadow-2xl overflow-hidden"
        >
          <div className="flex items-center justify-between px-3 pt-3 pb-1">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('dialer:dtmfKeypad.title')}</span>
            <button
              onClick={() => setShowKeypad(false)}
              className="w-6 h-6 flex items-center justify-center rounded-full text-muted-foreground/60 hover:bg-accent hover:text-foreground transition-colors"
              aria-label={t('dialer:dtmfKeypad.closeAria')}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          <div className="px-3 pb-2">
            {/* #95 — la croix et les boutons d'effacement etaient coupes. Cause :
                l'input porte `flex-1` mais pas `min-w-0`, et la valeur initiale
                `min-width: auto` d'un item flex l'empeche de retrecir sous son
                contenu. Il gardait 185 px et poussait les deux boutons hors du
                panneau (mesure : 14 px et 9 px au lieu de 28, contenu de 228 px
                pour 193 utiles), que `overflow-hidden` coupait ensuite.
                `min-w-0` sur l'input, `shrink-0` sur les boutons. */}
            <div className="flex items-center gap-1 surface-1 border border-border rounded-lg px-3 py-2">
              <input
                type="text"
                value={dtmfInput}
                onChange={(e) => {
                  const cleaned = e.target.value.replace(/[^0-9+*#\s()-]/g, '');
                  setDtmfInput(cleaned);
                }}
                onPaste={(e) => {
                  e.preventDefault();
                  const pasted = (e.clipboardData.getData('text') || '').replace(/[^0-9+*#\s()-]/g, '');
                  setDtmfInput((prev) => prev + pasted);
                }}
                placeholder={t('dialer:dtmfKeypad.numberPlaceholder')}
                className="flex-1 min-w-0 font-mono text-foreground text-base tracking-widest min-h-[1.5rem] bg-transparent outline-none placeholder:text-muted-foreground/40 placeholder:text-sm placeholder:font-sans placeholder:tracking-normal"
                autoComplete="off"
              />
              {dtmfInput.length > 0 && (
                <>
                  <button
                    onClick={handleBackspace}
                    className="w-7 h-7 shrink-0 flex items-center justify-center rounded text-muted-foreground/60 hover:text-foreground hover:bg-accent transition-colors"
                    aria-label={t('dialer:dtmfKeypad.backspaceAria')}
                  >
                    <Delete className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={handleClear}
                    className="w-7 h-7 shrink-0 flex items-center justify-center rounded text-muted-foreground/60 hover:text-red-500 hover:bg-red-50 transition-colors text-xs font-bold"
                    aria-label={t('dialer:dtmfKeypad.clearAllAria')}
                  >
                    C
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="px-3 pb-2 grid grid-cols-3 gap-1.5">
            {KEYPAD_ROWS.map((row) =>
              row.map((digit) => (
                <button
                  key={digit}
                  onClick={() => sendDTMF(digit)}
                  className="h-12 rounded-xl bg-muted hover:bg-accent active:bg-accent transition-colors flex flex-col items-center justify-center gap-0.5 select-none"
                >
                  <span className="text-lg font-semibold text-foreground leading-none">{digit}</span>
                  {KEY_LABELS[digit] && (
                    <span className="text-[9px] font-medium text-muted-foreground/60 tracking-widest leading-none">
                      {KEY_LABELS[digit]}
                    </span>
                  )}
                </button>
              )),
            )}
          </div>

          {/* Correction #85 — champ « From ». On pouvait composer un numero a la
              main sans jamais savoir DEPUIS QUEL numero l'appel partirait : le
              choix se faisait ailleurs, dans la barre du bas. Le voici a
              l'endroit ou on decide d'appeler. */}
          {activeCallerNumbers.length > 0 && (
            <div className="px-3 pb-2">
              <label className="block text-[10px] font-semibold uppercase tracking-wider mb-1"
                     style={{ color: 'var(--vf-text-3)' }}>
                {t('dialer:dtmfKeypad.fromLabel')}
              </label>
              <select
                value={selectedCallerId || activeCallerNumbers[0]?.phoneNumber || ''}
                onChange={(e) => setSelectedCallerId?.(e.target.value)}
                aria-label={t('dialer:dtmfKeypad.callerNumberAria')}
                className="w-full h-9 rounded-lg px-2 text-[13px] outline-none"
                style={{ background: 'var(--vf-surface-2)', border: '1px solid var(--vf-border)', color: 'var(--vf-text-1)' }}
              >
                {activeCallerNumbers.map((n) => (
                  <option key={n.phoneNumber} value={n.phoneNumber}>
                    {n.phoneNumber}{n.friendlyName ? `, ${n.friendlyName}` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="px-3 pb-3 flex gap-1.5">
            <button
              onClick={handleManualDial}
              disabled={dtmfInput.length < 4}
              className="flex-1 h-11 flex items-center justify-center gap-1.5 rounded-xl bg-green-500 hover:bg-green-600 active:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold text-sm transition-colors"
              aria-label={t('dialer:dtmfKeypad.callAria')}
            >
              <Phone className="w-4 h-4" />
              {t('common:actions.call')}
            </button>
            <button
              onClick={handleAddDigit}
              className="w-11 h-11 flex items-center justify-center rounded-xl bg-muted hover:bg-accent active:bg-accent transition-colors text-muted-foreground"
              aria-label={t('dialer:dtmfKeypad.addPlusAria')}
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
